import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'

import { axiosInstance } from '../../../helpers'
import { LeaseAuditDetail, LeaseAuditStep, TermStatusFilter } from '../types'

export interface UseLeaseAuditDetailOptions {
    auditId: number
    enabled?: boolean
}

export interface UpdateLeaseAuditParams {
    name?: string
    current_step?: LeaseAuditStep
    latest_ready_step?: LeaseAuditStep
    term_status_filter?: TermStatusFilter
}

export interface TermStatusCountsResponse {
    past: number
    current: number
    future: number
}

export const useLeaseAuditDetail = (options: UseLeaseAuditDetailOptions) => {
    const { auditId, enabled = true } = options
    const queryClient = useQueryClient()

    // Fetch lease audit detail
    const { data: leaseAuditData, isLoading, error, refetch } = useQuery<
        AxiosResponse<LeaseAuditDetail>,
        Error
    >({
        queryKey: ['leaseAuditDetail', auditId],
        queryFn: () => axiosInstance.get(`/lease-audit/${auditId}/`),
        enabled: enabled && !!auditId,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    // Update lease audit mutation
    const updateMutation = useMutation({
        mutationFn: (params: UpdateLeaseAuditParams) =>
            axiosInstance.patch(`/lease-audit/${auditId}/`, params),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['leaseAuditDetail', auditId],
            })
            queryClient.invalidateQueries({
                queryKey: ['auditResults', auditId],
            })
            queryClient.invalidateQueries({
                queryKey: ['rentRoll', auditId],
            })
            queryClient.invalidateQueries({
                queryKey: ['residentTerms', auditId],
            })
        },
    })

    // Get Term Status Counts mutation
    const getTermStatusCountsMutation = useMutation({
        mutationFn: () =>
            axiosInstance.get(`/lease-audit/${auditId}/term-status-counts/`),
        onSuccess: (data) => {
            queryClient.setQueryData(['termStatusCounts', auditId], data.data)
        },
    })

    // Generate report mutation
    const generateReportMutation = useMutation({
        mutationFn: () =>
            axiosInstance.post(`/lease-audit/${auditId}/generate-report/`),
    })

    const leaseAudit = leaseAuditData?.data
    const termStatusCountsData = queryClient.getQueryData([
        'termStatusCounts',
        auditId,
    ])
    return {
        // Data
        leaseAudit,
        termStatusCounts: termStatusCountsData as TermStatusCountsResponse,
        isLoadingTermStatusCounts: getTermStatusCountsMutation.isLoading,
        termStatusCountsError: getTermStatusCountsMutation.error,

        // Loading states
        isLoading,
        isUpdating: updateMutation.isLoading,
        isGeneratingReport: generateReportMutation.isLoading,

        // Error states
        error,
        updateError: updateMutation.error,

        // Actions
        refetch,
        updateLeaseAudit: updateMutation.mutate,
        getTermStatusCounts: getTermStatusCountsMutation.mutate,
        generateReport: generateReportMutation.mutate,
    }
}
